import React, { useCallback } from 'react';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { Button, Content, Flex, Pane, PortalOverlay, Text, Toast } from '@leverege/ui-elements';
import { ModelEditorFactory, Toolbar } from '@leverege/ui-plugin';
import { GlobalState, Selection } from '@leverege/ui-redux';
import SplitPane from 'split-pane-react';
import { useEditorVisibility, useRsrcImage, useSelectedDialogContext, useSelectedDialogKeyBindings, useSplitPaneSizes, useMouseMoveDelay } from "./hooks/index.js";
import RsrcDetailRendererFactory from "../RsrcDetailRendererFactory.js";
import S from './SelectedDialog.css';
import Rsrc from "../../models/Rsrc.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function SelectedDialog({
  cannotCycle,
  dataViewerId,
  delegate,
  hasCycled,
  onClose,
  onNextItem,
  onPreviousItem,
  overrideModel,
  overrides,
  paginator,
  path,
  reloadData,
  selectedIndex,
  selection,
  selectionKey,
  settings,
  show
}) {
  const selected = selection?.items?.[0];
  const onChange = useCallback(async evt => {
    const {
      value: valueRaw,
      oldValue: oldValueRaw
    } = evt;
    const value = Rsrc.toObjRef(valueRaw);
    const oldValue = Rsrc.toObjRef(oldValueRaw);
    const patch = [];
    Object.entries(value.data).forEach(([path, value]) => {
      if (value !== oldValue.data[path]) {
        patch.push({
          path,
          value
        });
      }
    });
    const item = await GlobalState.dispatch(delegate.item(Rsrc.getPath(oldValue)).update(patch));
    if (value.data.path !== oldValue.data.path) {
      GlobalState.dispatch(Selection.set(selectionKey, [delegate.item(value.data.path).toObjectRef(item)]));
    }
  }, [delegate, selectionKey]);
  const {
    sizes,
    onChangeSizes
  } = useSplitPaneSizes({
    dataViewerId,
    overrideModel,
    overrides
  });
  const {
    editorVisible,
    onToggleEditor
  } = useEditorVisibility({
    dataViewerId,
    overrideModel,
    overrides
  });
  const {
    matchContext,
    toolbarCxt
  } = useSelectedDialogContext({
    delegate,
    editorVisible,
    onToggleEditor,
    paginator,
    reloadData,
    selection,
    detail: settings?.detail,
    onClose
  });
  const {
    src,
    loading,
    ref
  } = useRsrcImage({
    item: selected,
    delegate
  });
  const {
    moving,
    onMouseMove,
    hoverRef
  } = useMouseMoveDelay();
  useSelectedDialogKeyBindings({
    onNextItem,
    onPreviousItem,
    onToggleEditor
  });
  if (!selected) {
    return null;
  }
  const viewer = /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsx(TitleBar, {
      onBack: onClose,
      hoverRef: hoverRef,
      className: classnames(S.titleBar, moving && S.visible),
      title: selected.data.path,
      variant: "rsrcImageDetailTitle|pageTitle",
      icon: "fa fa-fw fa-arrow-left",
      children: /*#__PURE__*/_jsx(Toolbar, {
        variant: "rsrcActionBar",
        layoutVariant: "flex:rowSReverse",
        prefer: "icon",
        hasContextMenu: true,
        contextMenuHideWhenEmpty: true,
        matchContext: {
          ...matchContext,
          use: 'actionBar'
        },
        context: {
          ...toolbarCxt,
          target: selection?.items
        }
      })
    }), RsrcDetailRendererFactory.create(settings?.detail, {
      rsrc: selected,
      src,
      loading,
      path,
      width: sizes[0],
      onToggleEditor,
      context: toolbarCxt
    })]
  });
  const editor = ModelEditorFactory.create(selected, {
    onChange,
    onCancel: onClose,
    inContent: false,
    detailModel: settings?.detail,
    details: settings?.details,
    width: sizes[1]?.includes?.('%') ? null : sizes[1],
    onNextItem,
    onPreviousItem,
    delegate,
    item: selected
  });
  return /*#__PURE__*/_jsx(PortalOverlay, {
    animation: "fade",
    onClose: onClose,
    show: show,
    children: /*#__PURE__*/_jsxs(Pane, {
      variant: "rsrcImageDetail",
      className: S.contentHolder,
      onMouseMove: onMouseMove,
      onClick: onMouseMove,
      children: [/*#__PURE__*/_jsx(Content, {
        variant: "rsrcImage",
        horizontal: true,
        domRef: ref,
        children: /*#__PURE__*/_jsx(Content.Area, {
          className: S.contentHolder,
          children: /*#__PURE__*/_jsxs(SplitPane, {
            allowResize: editorVisible,
            onChange: onChangeSizes,
            sizes: editorVisible ? sizes : ['100%', 0],
            children: [/*#__PURE__*/_jsxs("div", {
              className: S.viewerHolder,
              children: [/*#__PURE__*/_jsx(HasCycledIndicator, {
                hasCycled: hasCycled,
                cannotCycle: cannotCycle
              }), viewer, paginator.pageItems.length > 1 && /*#__PURE__*/_jsx(NextBackButtons, {
                onNextItem: onNextItem,
                onPreviousItem: onPreviousItem,
                selectedIndex: selectedIndex,
                count: paginator.pageItems.length,
                moving: moving
              })]
            }), editor || /*#__PURE__*/_jsx("div", {})]
          })
        })
      }), /*#__PURE__*/_jsx(Toast, {
        containerId: "selectedDialog"
      })]
    })
  });
}
function NextBackButtons({
  count,
  moving,
  onNextItem,
  onPreviousItem,
  selectedIndex
}) {
  return /*#__PURE__*/_jsxs(React.Fragment, {
    children: [/*#__PURE__*/_jsx(Pane, {
      className: classnames(S.carouselButton, S.carouselButtonPrevious),
      onClick: onPreviousItem,
      children: /*#__PURE__*/_jsx("div", {
        className: S.iconHolder,
        children: /*#__PURE__*/_jsx("i", {
          className: " fa fa-chevron-left"
        })
      })
    }), /*#__PURE__*/_jsx(Pane, {
      className: classnames(S.carouselButton, S.carouselButtonNext),
      onClick: onNextItem,
      children: /*#__PURE__*/_jsx("div", {
        className: S.iconHolder,
        children: /*#__PURE__*/_jsx("i", {
          className: " fa fa-chevron-right"
        })
      })
    }), /*#__PURE__*/_jsxs(Pane, {
      className: classnames(S.pagination, moving && S.visible),
      children: [selectedIndex + 1, " of ", count]
    })]
  });
}
function HasCycledIndicator({
  hasCycled,
  cannotCycle
}) {
  return /*#__PURE__*/_jsx(Pane, {
    variant: "rsrcHasCycled",
    className: classnames(S.hasCycledView, (hasCycled || cannotCycle) && S.hasCycled),
    children: /*#__PURE__*/_jsx("i", {
      className: classnames(S.hasCycledIcon, hasCycled && 'fa-sharp fa-solid fa-repeat', cannotCycle && 'fa-sharp fa-solid fa-ban')
    })
  });
}
function TitleBar({
  title,
  style,
  icon,
  toolbarProps,
  children,
  hoverRef,
  onBack,
  className
}) {
  return /*#__PURE__*/_jsxs(Pane, {
    className: className,
    variant: "rsrcImageDetailTitle",
    layout: "flex:rowSpaceBetweenCenter",
    domRef: hoverRef,
    style: style,
    children: [/*#__PURE__*/_jsxs(Flex, {
      variant: "rowM",
      children: [/*#__PURE__*/_jsx(Button, {
        onClick: onBack,
        variant: "rsrcImageDetailTitle",
        icon: icon
      }), /*#__PURE__*/_jsx(Text, {
        variant: "rsrcImageDetailTitle",
        children: title
      })]
    }), /*#__PURE__*/_jsxs(Flex, {
      variant: "rsrcImageDetailControls",
      className: S.controls,
      children: [toolbarProps && /*#__PURE__*/_jsx(Toolbar, {
        ...toolbarProps
      }), children]
    })]
  });
}
export default connect((state, props) => {
  const {
    selectionKey
  } = props;
  return {
    selection: Selection.get(state, selectionKey)
  };
})(SelectedDialog);